/**
 * Sudoku data model — the shape a puzzle has before anyone plays it.
 *
 * Split out of `apps/games-web/src/games/sudoku/engine.ts`, which keeps the
 * React hook, the board/candidate/undo state, and the timer. What lives here is
 * the part a print renderer, a grader, or a generation service needs: the digit
 * domain, the four difficulty tiers, the givens-plus-solution puzzle, the
 * compact bank encoding, and the row/col/box coordinate math.
 *
 * Difficulty is defined by the hardest *technique* a human must use, never by
 * clue count — see `grader.ts`, which is the source of truth for the mapping
 * and re-verifies every banked puzzle in `@rendever/puzzle-content`.
 */
/** Board side length and cell count. A standard 9x9 grid. */
export const SUDOKU_N = 9;
export const SUDOKU_SIZE = 81;
/** The four tiers in ascending order — handy for iteration and UI ordering. */
export const SUDOKU_DIFFICULTIES = [
    'easy',
    'medium',
    'hard',
    'expert',
];
// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------
/** Decode an 81-char givens string into cell values (0 = empty). */
export function decodeGivens(s) {
    return s.split('').map((ch) => (ch === '.' || ch === '0' ? 0 : Number(ch)));
}
/** Decode an 81-char solution string into digits. */
export function decodeSolution(s) {
    return s.split('').map((ch) => Number(ch));
}
/** Decode one banked puzzle into the playable/printable {@link SudokuPuzzle}. */
export function decodePuzzle(p, difficulty) {
    return {
        difficulty,
        givens: decodeGivens(p.givens),
        solution: decodeSolution(p.solution),
    };
}
/** Decode a whole bank, tier by tier. */
export function decodeBank(bank) {
    return {
        easy: bank.easy.map((p) => decodePuzzle(p, 'easy')),
        medium: bank.medium.map((p) => decodePuzzle(p, 'medium')),
        hard: bank.hard.map((p) => decodePuzzle(p, 'hard')),
        expert: bank.expert.map((p) => decodePuzzle(p, 'expert')),
    };
}
// ---------------------------------------------------------------------------
// Coordinate math
// ---------------------------------------------------------------------------
/** Row, column and 3x3 box of a flat row-major index. */
export function rc(index) {
    const row = Math.floor(index / SUDOKU_N);
    const col = index % SUDOKU_N;
    const box = Math.floor(row / 3) * 3 + Math.floor(col / 3);
    return { row, col, box };
}
/** Flat row-major index for a row/col pair. */
export function idx(row, col) {
    return row * SUDOKU_N + col;
}
/** Indices of the row/col/box peers for `index` (excluding self). */
export function peers(index) {
    const { row, col, box } = rc(index);
    const set = new Set();
    for (let i = 0; i < SUDOKU_N; i += 1) {
        set.add(idx(row, i));
        set.add(idx(i, col));
    }
    const boxRow = Math.floor(box / 3) * 3;
    const boxCol = (box % 3) * 3;
    for (let r = 0; r < 3; r += 1) {
        for (let c = 0; c < 3; c += 1) {
            set.add(idx(boxRow + r, boxCol + c));
        }
    }
    set.delete(index);
    return Array.from(set);
}
//# sourceMappingURL=types.js.map